/**
 * FinalTest.tsx
 * -------------
 * Renders the final quiz for the tutorial.
 * - Multiple-answer, multilingual questions.
 * - Partial scoring: +1 for each correct, -1 for each incorrect, min 0, normalized per question.
 * - Progress bar, pass/fail logic, and redirect on success.
 * - Integrates with ProgressContext for global state.
 */


import React, { useEffect, useState } from 'react';
import Box from './common/Box';
import Button from './common/Button';
import ProgressBar from './ProgressBar';
import { useHistory } from 'react-router-dom';
import { useProgress } from '../context/ProgressContext';

const FinalTest = () => {
  // State for quiz progress and answers
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // Context and navigation
  const { setStep, setTestPassed, language } = useProgress();
  const history = useHistory();

  // Set the current step in global progress context
  useEffect(() => {
    setStep(3);
  }, [setStep]);

  // Language helpers and types
  const lang = (['en', 'fi', 'sv'].includes(language) ? language : 'fi') as 'en' | 'fi' | 'sv';
  type Lang = 'en' | 'fi' | 'sv';
  type Option = Record<Lang, string>;
  type Question = {
    question: Record<Lang, string>;
    options: Option[];
    answers: number[]; // Indices of correct answers
  };

  // Quiz questions and answers (multilingual)
  const questions: Question[] = [
    {
      question: {
        en: 'What are the safest ways to use a Finnish sauna?',
        fi: 'Mitkä ovat turvallisimmat tavat käyttää suomalaista saunaa?',
        sv: 'Vilka är de säkraste sätten att använda en finsk bastu?',
      },
      options: [
        {
          en: 'Heat the sauna properly and cool down gradually',
          fi: 'Lämmitä sauna kunnolla ja jäähdyttele rauhassa',
          sv: 'Värm bastun ordentligt och svalka dig gradvis',
        },
        {
          en: "Stay inside for as long as you feel like you can't",
          fi: 'Pysy saunassa niin kauan kuin jaksat',
          sv: 'Stanna i bastun så länge du orkar',
        },
        {
          en: 'Use the sauna as a drying rack, utilizing the heat from the sauna to dry clothes',
          fi: 'Käytä saunaa pyykinkuivaustelineenä hyödyntäen saunan lämpöä',
          sv: 'Använd bastun som torkställning för kläder',
        },
        {
          en: 'Take sips of water or sports drinks before, during and after sauna visit',
          fi: 'Juo vettä tai urheilujuomaa ennen, aikana ja jälkeen saunomisen',
          sv: 'Drick vatten eller sportdryck före, under och efter bastubadet',
        },
        {
          en: 'Do not extend your time in the sauna past 15-20 minutes',
          fi: 'Älä ole saunassa yli 15–20 minuuttia kerrallaan',
          sv: 'Stanna inte i bastun längre än 15–20 minuter åt gången',
        },
        {
          en: 'Pour cold water on the heater constantly for a faster heat up in the sauna',
          fi: 'Kaada jatkuvasti kylmää vettä kiukaalle nopeuttaaksesi lämmitystä',
          sv: 'Häll kallt vatten på aggregatet hela tiden för snabbare uppvärmning',
        },
      ],
      answers: [0, 3, 4],
    },
    {
      question: {
        en: 'How to stay safe in the cold climate of Finland?',
        fi: 'Miten pysyt turvassa Suomen kylmässä ilmastossa?',
        sv: 'Hur håller du dig säker i det kalla finska klimatet?',
      },
      options: [
        { en: 'Proper clothing', fi: 'Oikeanlainen pukeutuminen', sv: 'Rätt klädsel' },
        { en: 'Internet connection', fi: 'Internet-yhteys', sv: 'Internetanslutning' },
        {
          en: 'TV channels available',
          fi: 'Saatavilla olevat TV-kanavat',
          sv: 'Tillgängliga TV-kanaler',
        },
        {
          en: 'Windows and doors firmly shut',
          fi: 'Ikkunat ja ovet tiiviisti kiinni',
          sv: 'Fönster och dörrar ordentligt stängda',
        },
        { en: 'Nearby restaurants', fi: 'Läheiset ravintolat', sv: 'Närliggande restauranger' },
        {
          en: 'Always wearing reflectors',
          fi: 'Heijastimen käyttö aina ulkona',
          sv: 'Använd alltid reflexer utomhus',
        },
        {
          en: 'Wearing good non-slip winter boots',
          fi: 'Hyvät liukuestolliset talvikengät',
          sv: 'Bra halkfria vinterstövlar',
        },
      ],
      answers: [0, 3, 5, 6],
    },
    {
      question: {
        en: 'What are some of the key house rules?',
        fi: 'Mitkä ovat tärkeimpiä talon sääntöjä?',
        sv: 'Vilka är några av de viktigaste husreglerna?',
      },
      options: [
        {
          en: "Follow the house rules and respect other guests' peace and privacy",
          fi: 'Noudata talon sääntöjä ja kunnioita muiden vieraiden rauhaa ja yksityisyyttä',
          sv: 'Följ husets regler och respektera andra gästers lugn och integritet',
        },
        {
          en: 'Leave the sauna door open at all times',
          fi: 'Jätä saunan ovi aina auki',
          sv: 'Lämna bastudörren öppen hela tiden',
        },
        {
          en: 'Contact the host if real trouble arises',
          fi: 'Ota yhteyttä isäntään, jos ilmenee todellisia ongelmia',
          sv: 'Kontakta värden om det uppstår riktiga problem',
        },
        {
          en: 'Use only cold water on the heater',
          fi: 'Käytä kiukaalla vain kylmää vettä',
          sv: 'Använd endast kallt vatten på aggregatet',
        },
        {
          en: 'Use heating devices according to the given instructions',
          fi: 'Käytä lämmityslaitteita annettujen ohjeiden mukaisesti',
          sv: 'Använd värmeanordningar enligt givna instruktioner',
        },
      ],
      answers: [0, 2, 4],
    },
  ];

  const t = {
    finalTestTitle: { en: 'Final Test', fi: 'Loppukoe', sv: 'Slutprov' },
    intro: {
      en: 'Please answer the following questions to complete the tutorial.',
      fi: 'Vastaa seuraaviin kysymyksiin suorittaaksesi oppaan.',
      sv: 'Svara på följande frågor för att slutföra handledningen.',
    },
    testCompleted: { en: 'Test Completed!', fi: 'Koe suoritettu!', sv: 'Testet slutfört!' },
    score: {
      en: (s: number, t: number) => `Your score: ${s} out of ${t}`,
      fi: (s: number, t: number) => `Pisteesi: ${s} / ${t}`,
      sv: (s: number, t: number) => `Din poäng: ${s} av ${t}`,
    },
    pass: {
      en: 'You passed! Redirecting to unlock Wi-Fi...',
      fi: 'Läpäisit! Siirrytään avaamaan Wi-Fi...',
      sv: 'Du klarade det! Omdirigerar till upplåsning av Wi-Fi...',
    },
    fail: {
      en: 'You did not pass. Please try again.',
      fi: 'Et läpäissyt. Yritä uudelleen.',
      sv: 'Du klarade det inte. Försök igen.',
    },
    next: { en: 'Next', fi: 'Seuraava', sv: 'Nästa' },
    submit: { en: 'Submit', fi: 'Lähetä', sv: 'Skicka' },
    back: { en: 'Back', fi: 'Takaisin', sv: 'Tillbaka' },
    failBack: {
      en: 'Back to Crash Course',
      fi: 'Takaisin pikaopastukseen',
      sv: 'Tillbaka till snabbkursen',
    },
  };
  /**
   * Handles toggling of answer options (checkboxes).
   * Adds or removes the selected option index from the selectedOptions array.
   */
  const handleOptionToggle = (index: number) => {
    setSelectedOptions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Handles submission of the current question.
   * Calculates partial score: +1 for each correct, -1 for each incorrect, min 0,
   * then normalizes to the number of correct answers for fair scoring.
   * Advances to the next question or submits the test.
   */
  const handleSubmit = () => {
    const correctAnswers = questions[currentQuestion].answers;
    const selectedCorrect = selectedOptions.filter((idx) => correctAnswers.includes(idx));
    const selectedIncorrect = selectedOptions.filter((idx) => !correctAnswers.includes(idx));

    // Partial score: +1 for each correct, -1 for each incorrect, min 0
    let partialScore = selectedCorrect.length - selectedIncorrect.length;
    if (partialScore < 0) partialScore = 0;

    // Normalize to a fraction of the question's total correct answers
    let normalizedScore = partialScore / correctAnswers.length;

    setScore((s) => s + normalizedScore);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((q) => q + 1);
      setSelectedOptions([]);
    } else {
      setSubmitted(true);
    }
  };

  /**
   * Effect: On test submission, determines pass/fail and redirects if passed.
   */
  useEffect(() => {
    if (submitted) {
      const passed = score >= 2;
      setTestPassed(passed);
      if (passed) setTimeout(() => history.push('/unlock'), 1500);
    }
  }, [submitted, score, setTestPassed, history]);

  return (
    <Box className="m-5">
      {submitted ? (
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-bold text-blue-800">{t.testCompleted[lang]}</h2>
          <p className="text-blue-800 font-semibold">{t.score[lang](score, questions.length)}</p>
          <p className={`font-semibold ${score >= 2 ? 'text-green-600' : 'text-red-600'}`}>
            {score >= 2 ? t.pass[lang] : t.fail[lang]}
          </p>
          <Button gradient className="self-center" onClick={() => history.push('/crash-course')}>
            {t.failBack[lang]}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl md:text-4xl font-extrabold text-blue-900 text-center">
            {t.finalTestTitle[lang]}
          </h1>
          <p className="text-lg text-center text-blue-800 max-w-xs mx-auto font-medium">
            {t.intro[lang]}
          </p>
          <ProgressBar progress={currentQuestion / questions.length} />
          <h2 className="text-xl font-semibold text-blue-900 text-center">
            {questions[currentQuestion].question[lang]}
          </h2>
          <div className="flex flex-col gap-2">
            {questions[currentQuestion].options.map((option, index) => (
              <label
                key={option[lang]}
                className="flex items-center bg-blue-50 rounded-lg px-3 py-2 cursor-pointer hover:bg-blue-100 transition"
              >
                <input
                  type="checkbox"
                  checked={selectedOptions.includes(index)}
                  onChange={() => handleOptionToggle(index)}
                  value={index}
                  className="mr-3 accent-blue-600"
                />
                {option[lang]}
              </label>
            ))}
          </div>
          <div className="flex justify-evenly">
            <Button
              gradient
              onClick={
                currentQuestion === 0
                  ? () => history.push('/crash-course')
                  : () => {
                      setCurrentQuestion((q) => q - 1);
                      setSelectedOptions([]);
                    }
              }
            >
              {t.back[lang]}
            </Button>
            <Button
              gradient={false}
              onClick={handleSubmit}
              className={`${
                selectedOptions.length === 0
                  ? 'bg-gradient-to-t bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600'
              } text-white`}
              disabled={selectedOptions.length === 0}
            >
              {currentQuestion < questions.length - 1 ? t.next[lang] : t.submit[lang]}
            </Button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default FinalTest;
